import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BACKEND_URL } from "../config";
import "./NutritionHub.css";

const InlineSpinner = ({ size = 16 }) => (
  <span
    aria-label="Loading"
    role="status"
    style={{
      width: size,
      height: size,
      display: "inline-block",
      borderRadius: "50%",
      border: "2px solid rgba(0,0,0,0.15)",
      borderTopColor: "rgba(0,0,0,0.55)",
      animation: "spin 0.8s linear infinite",
      verticalAlign: "middle",
    }}
  />
);

const SpinnerKeyframes = () => (
  <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
);

function useIsMobile(breakpoint = 809) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function MealCardCarousel({ items, renderItem }) {
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
    if (trackRef.current) trackRef.current.scrollLeft = 0;
  }, [items]);

  const scrollToIndex = useCallback((index) => {
    const el = trackRef.current;
    if (!el) return;
    const slides = Array.from(el.children);
    const next = clamp(index, 0, Math.max(slides.length - 1, 0));
    const slide = slides[next];
    if (!slide) return;
    el.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
    setActiveIndex(next);
  }, []);

  const handleScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const slides = Array.from(el.children);
    if (!slides.length) return;
    const center = el.scrollLeft + el.clientWidth / 2;
    let bestIdx = 0;
    let bestDist = Infinity;
    slides.forEach((slide, idx) => {
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const d = Math.abs(center - slideCenter);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = idx;
      }
    });
    setActiveIndex(bestIdx);
  }, []);

  if (!items.length) return null;

  return (
    <div className="nh-mobile-meal-carousel">
      <button
        type="button"
        className="nh-carousel-arrow nh-carousel-arrow--left"
        onClick={() => scrollToIndex(activeIndex - 1)}
        disabled={activeIndex === 0}
        aria-label="Previous meal"
      >
        ‹
      </button>

      <div ref={trackRef} className="nh-carousel-track" onScroll={handleScroll}>
        {items.map((item, idx) => (
          <div className="nh-carousel-slide" key={idx}>
            {renderItem(item, idx)}
          </div>
        ))}
      </div>

      <button
        type="button"
        className="nh-carousel-arrow nh-carousel-arrow--right"
        onClick={() => scrollToIndex(activeIndex + 1)}
        disabled={activeIndex >= items.length - 1}
        aria-label="Next meal"
      >
        ›
      </button>

      {items.length > 1 ? (
        <div className="nh-carousel-dots" role="tablist" aria-label="Meals">
          {items.map((_, idx) => (
            <button
              key={idx}
              type="button"
              className={`nh-carousel-dot ${idx === activeIndex ? "is-active" : ""}`}
              aria-label={`Go to meal ${idx + 1}`}
              aria-selected={idx === activeIndex}
              onClick={() => scrollToIndex(idx)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function NutritionHub({ token, onBackToLogging, showToast }) {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const [plan, setPlan] = useState(null);
  const [draft, setDraft] = useState({
    calories_target: "",
    protein_g: "",
    carbs_g: "",
    fats_g: "",
    fiber_g: "",
    water_oz: "",
  });

  const [mealPlan, setMealPlan] = useState(null);
  const [mealDays, setMealDays] = useState(7);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [mealChangeNote, setMealChangeNote] = useState("");
  const [mealActionModal, setMealActionModal] = useState(false);
  const [mealActionTarget, setMealActionTarget] = useState(null);
  const [mealReplaceSearch, setMealReplaceSearch] = useState("");
  const [mealReplaceLibrary, setMealReplaceLibrary] = useState([]);
  const [mealReplaceRecipeId, setMealReplaceRecipeId] = useState("");
  const [cancelingMealGen, setCancelingMealGen] = useState(false);

  const [autoMealRegen, setAutoMealRegen] = useState(() => {
    try { return localStorage.getItem("ht_autoMealRegen") === "1"; } catch { return false; }
  });
  const [autoGroceryAfterMeal, setAutoGroceryAfterMeal] = useState(() => {
    try { return localStorage.getItem("ht_autoGroceryAfterMeal") === "1"; } catch { return false; }
  });

  const [grocery, setGrocery] = useState(null);
  const [showGroceryModal, setShowGroceryModal] = useState(false);
  const [showAddGroceryModal, setShowAddGroceryModal] = useState(false);
  const [groceryDetailModal, setGroceryDetailModal] = useState(null);

  const [groceryDraft, setGroceryDraft] = useState({});
  const [newGroceryItem, setNewGroceryItem] = useState({
    name: "",
    quantity: "",
    unit: "",
    category: "Other",
  });

  const [bgStatus, setBgStatus] = useState(null);
  const busy = aiBusy || (bgStatus === "running" && !cancelingMealGen);

  const [replaceFrom, setReplaceFrom] = useState("");
  const [replaceTo, setReplaceTo] = useState("");

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchFirstOk = async (urls, opts) => {
    let lastErr = null;
    for (const url of urls) {
      if (!url) continue;
      try {
        const res = await fetch(url, { ...(opts || {}), cache: "no-store" });
        if (!res.ok) {
          lastErr = new Error(`HTTP ${res.status}`);
          continue;
        }
        const text = await res.text();
        if (!text) return null;
        try { return JSON.parse(text); } catch { return text; }
      } catch (e) {
        lastErr = e;
      }
    }
    if (lastErr) throw lastErr;
    return null;
  };

  const loadNutritionSettings = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/nutrition/settings`, { headers: authHeaders, cache: "no-store" });
      if (!res.ok) return;
      const data = await res.json();
      const s = data?.settings || {};
      setAutoMealRegen(Boolean(s.auto_regen));
      setAutoGroceryAfterMeal(Boolean(s.auto_grocery));
      if (s.days != null) setMealDays(Number(s.days) || 7);
    } catch {}
  }, [token, authHeaders]);

  const saveNutritionSettings = useCallback(async (patch) => {
    if (!token) return;
    try {
      const next = {
        auto_regen: (patch?.auto_regen ?? autoMealRegen) ? true : false,
        auto_grocery: (patch?.auto_grocery ?? autoGroceryAfterMeal) ? true : false,
        days: Number(patch?.days ?? mealDays ?? 7) || 7,
      };
      const res = await fetch(`${BACKEND_URL}/api/nutrition/settings`, {
        method: "PUT",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify(next),
        cache: "no-store",
      });
      if (res.ok) {
        const data = await res.json();
        const s = data?.settings || next;
        setAutoMealRegen(Boolean(s.auto_regen));
        setAutoGroceryAfterMeal(Boolean(s.auto_grocery));
        setMealDays(Number(s.days) || 7);
      }
    } catch {}
  }, [token, authHeaders, autoMealRegen, autoGroceryAfterMeal, mealDays]);

  useEffect(() => {
    try { localStorage.setItem("ht_autoMealRegen", autoMealRegen ? "1" : "0"); } catch {}
    if (!autoMealRegen) setAutoGroceryAfterMeal(false);
  }, [autoMealRegen]);

  useEffect(() => {
    try { localStorage.setItem("ht_autoGroceryAfterMeal", autoGroceryAfterMeal ? "1" : "0"); } catch {}
  }, [autoGroceryAfterMeal]);

  const loadPlan = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/nutrition/plan/me`, { headers: authHeaders });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const p = data.plan || data.nutrition_plan || null;
      setPlan(p);
      const t = p?.targets || {};
      setDraft({
        calories_target: t.calories_target ?? "",
        protein_g: t.protein_g ?? "",
        carbs_g: t.carbs_g ?? "",
        fats_g: t.fats_g ?? "",
        fiber_g: t.fiber_g ?? "",
        water_oz: t.water_oz ?? "",
      });
    } catch (e) {
      console.warn("Load nutrition plan failed:", e?.message || e);
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [token, authHeaders]);

  const loadExistingMealPlan = useCallback(async () => {
    if (!token) return;
    try {
      const data = await fetchFirstOk(
        [
          `${BACKEND_URL}/api/nutrition/meal-plan/active`,
          `${BACKEND_URL}/api/nutrition/meal-plan/latest`,
          `${BACKEND_URL}/api/nutrition/meal-plan?active=true`,
          `${BACKEND_URL}/api/nutrition/meal-plan/list?limit=1`,
        ],
        { headers: authHeaders, cache: "no-store" }
      );
      const mp =
        data?.meal_plan ||
        data?.plan ||
        (Array.isArray(data?.meal_plans) ? data.meal_plans[0] : null) ||
        (Array.isArray(data?.plans) ? data.plans[0] : null);

      if (mp && mp.meal_plan_id) {
        setMealPlan(mp);
        setBgStatus("completed");
      } else {
        setMealPlan(null);
        setBgStatus(null);
      }
    } catch {}
  }, [token, authHeaders]);

  const loadExistingGroceryList = useCallback(async () => {
    if (!token) return;
    try {
      const data = await fetchFirstOk(
        [
          `${BACKEND_URL}/api/nutrition/grocery-list/active`,
          `${BACKEND_URL}/api/nutrition/grocery-list/latest`,
          `${BACKEND_URL}/api/nutrition/grocery-list?active=true`,
          `${BACKEND_URL}/api/nutrition/grocery-list/list?limit=1`,
        ],
        { headers: authHeaders, cache: "no-store" }
      );
      const gl =
        data?.grocery_list ||
        data?.list ||
        (Array.isArray(data?.grocery_lists) ? data.grocery_lists[0] : null) ||
        (Array.isArray(data?.lists) ? data.lists[0] : null);
      setGrocery(gl && gl.grocery_list_id ? gl : null);
    } catch {}
  }, [token, authHeaders]);

  const pollMealPlanBg = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/nutrition/meal-plan/active`, {
        headers: authHeaders,
        cache: "no-store",
      });
      if (!res.ok) {
        setBgStatus((prev) => (prev === "running" ? null : prev));
        return;
      }

      const data = await res.json();
      const mp = data?.meal_plan || null;
      if (!mp) {
        setBgStatus(null);
        return;
      }

      setMealPlan(mp);
      const st = mp?.meta?.bg_status || mp?.bg_status || mp?.status || null;
      setBgStatus(st);

      if (st === "done" || st === "completed" || st === "ready") {
        setBgStatus(null);
        if (autoMealRegen && autoGroceryAfterMeal && mp?.meal_plan_id) {
          try {
            await fetch(
              `${BACKEND_URL}/api/nutrition/grocery-list/from-meal-plan/${encodeURIComponent(mp.meal_plan_id)}`,
              { method: "POST", headers: authHeaders }
            );
          } catch {}
        }
        await loadExistingMealPlan();
        await loadExistingGroceryList();
        showToast?.("✅ Meal plan is ready!", "success");
      } else if (st === "cancelled") {
        setBgStatus(null);
        showToast?.("🛑 Meal plan generation was cancelled.", "success");
      } else if (st === "error" || st === "failed") {
        setBgStatus(null);
        showToast?.("⚠️ Meal plan background generation failed. Try again.", "error");
      }
    } catch {
      setBgStatus((prev) => (prev === "running" ? null : prev));
    }
  }, [token, authHeaders, autoMealRegen, autoGroceryAfterMeal, loadExistingMealPlan, loadExistingGroceryList, showToast]);

  useEffect(() => {
    if (!token) return;
    loadPlan();
    loadExistingMealPlan();
    loadExistingGroceryList();
    loadNutritionSettings();
  }, [token, loadPlan, loadExistingMealPlan, loadExistingGroceryList, loadNutritionSettings]);

  useEffect(() => {
    if (!token || bgStatus !== "running") return;
    const id = setInterval(() => {
      pollMealPlanBg();
    }, 4000);
    return () => clearInterval(id);
  }, [bgStatus, token, pollMealPlanBg]);

  useEffect(() => {
    const totalDays = Array.isArray(mealPlan?.meals) ? mealPlan.meals.length : 0;
    if (!totalDays) {
      setSelectedDayIndex(0);
      return;
    }
    setSelectedDayIndex((prev) => Math.max(0, Math.min(prev, totalDays - 1)));
  }, [mealPlan]);

  const showMealPlanGeneratingBanner = !!mealPlan && (bgStatus === "running" || mealPlan?.bg_status === "running");

  const normalizeGroceryItem = (it, fallbackKey) => {
    const cat = (it?.category || it?.cat || "Other").toString();
    const name = (it?.name || "").toString();
    return {
      ...it,
      category: cat,
      name,
      item_id: it?.item_id || it?.id || fallbackKey,
      checked: Boolean(it?.checked),
      quantity: it?.quantity ?? "",
      unit: it?.unit ?? "",
    };
  };

  const syncGroceryDraftFromList = useCallback((gl) => {
    if (!gl) return;
    const next = {};
    const items = Array.isArray(gl.items)
      ? gl.items
      : Array.isArray(gl.groups)
      ? gl.groups.flatMap((g) => (g.items || []).map((it) => ({ ...it, category: it.category || g.category || "Other" })))
      : [];
    items.forEach((it, idx) => {
      const key = it?.item_id || it?.id || `${(it?.category || "Other")}-${(it?.name || "item")}-${idx}`;
      next[key] = normalizeGroceryItem(it, key);
    });
    setGroceryDraft(next);
  }, []);

  useEffect(() => {
    if (grocery) syncGroceryDraftFromList(grocery);
  }, [grocery, syncGroceryDraftFromList]);

  useEffect(() => {
    if (!mealActionModal || !mealActionTarget?.meal_type || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/nutrition/library/search?meal_type=${encodeURIComponent(mealActionTarget.meal_type)}`,
          { headers: authHeaders, cache: "no-store" }
        );
        if (!res.ok) {
          if (!cancelled) setMealReplaceLibrary([]);
          return;
        }
        const data = await res.json();
        if (!cancelled) setMealReplaceLibrary(Array.isArray(data?.recipes) ? data.recipes : []);
      } catch {
        if (!cancelled) setMealReplaceLibrary([]);
      }
    })();
    return () => { cancelled = true; };
  }, [mealActionModal, mealActionTarget?.meal_type, token, authHeaders]);

  const patchGroceryItem = async (groceryListId, itemId, patch) => {
    if (!token || !groceryListId || !itemId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/nutrition/grocery-list/${encodeURIComponent(groceryListId)}/items/${encodeURIComponent(itemId)}`,
        {
          method: "PATCH",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify(patch || {}),
          cache: "no-store",
        }
      );
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setGrocery(data.grocery_list || null);
      await loadExistingGroceryList();
    } catch (e) {
      showToast?.("Grocery update failed: " + (e?.message || String(e)), "error");
    } finally {
      setLoading(false);
    }
  };

  const addGroceryItem = async (groceryListId, item) => {
    if (!token || !groceryListId) return;
    const payload = {
      name: (item?.name || "").trim(),
      category: (item?.category || "Other").trim() || "Other",
      quantity: item?.quantity === "" ? "" : isNaN(Number(item?.quantity)) ? "" : Number(item?.quantity),
      unit: (item?.unit || "").trim(),
    };
    if (!payload.name) return;

    setLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/nutrition/grocery-list/${encodeURIComponent(groceryListId)}/items`,
        {
          method: "POST",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          cache: "no-store",
        }
      );
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setGrocery(data.grocery_list || null);
      setNewGroceryItem({ name: "", quantity: "", unit: "", category: "Other" });
      setShowAddGroceryModal(false);
      await loadExistingGroceryList();
      showToast?.("✅ Grocery item added.", "success");
    } catch (e) {
      showToast?.("Add item failed: " + (e?.message || String(e)), "error");
    } finally {
      setLoading(false);
    }
  };

  const openOrCreateGroceryList = async () => {
    if (!token) return;

    if (grocery?.grocery_list_id) {
      setShowGroceryModal(true);
      return;
    }

    if (!mealPlan?.meal_plan_id) {
      showToast?.("Generate a meal plan first to create a grocery list.", "info");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/nutrition/grocery-list/from-meal-plan/${encodeURIComponent(mealPlan.meal_plan_id)}`,
        {
          method: "POST",
          headers: authHeaders,
        }
      );
      if (!res.ok) throw new Error(await res.text());

      const data = await res.json();
      setGrocery(data?.grocery_list || null);
      await loadExistingGroceryList();
      setShowGroceryModal(true);
      showToast?.("✅ Grocery list created.", "success");
    } catch (e) {
      showToast?.("Create grocery list failed: " + (e?.message || String(e)), "error");
    } finally {
      setLoading(false);
    }
  };

  const saveTargets = async (useAI = false) => {
    if (!token) return;
    if (useAI) setAiBusy(true);
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/nutrition/ai/modify`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "change_targets",
          ai: !!useAI,
          targets: {
            calories_target: draft.calories_target === "" ? null : Number(draft.calories_target),
            protein_g: draft.protein_g === "" ? null : Number(draft.protein_g),
            carbs_g: draft.carbs_g === "" ? null : Number(draft.carbs_g),
            fats_g: draft.fats_g === "" ? null : Number(draft.fats_g),
            fiber_g: draft.fiber_g === "" ? null : Number(draft.fiber_g),
            water_oz: draft.water_oz === "" ? null : Number(draft.water_oz),
          },
          note: useAI ? "Optimize targets based on my goal and activity level." : "",
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      await res.json();
      await loadPlan();
      showToast?.("✅ Targets saved.", "success");
    } catch (e) {
      showToast?.("Save failed: " + (e?.message || String(e)), "error");
    } finally {
      if (useAI) setAiBusy(false);
      setLoading(false);
    }
  };

  const aiSuggestTargets = async () => {
    setAiBusy(true);
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/nutrition/plan/ai/generate`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const p = data.plan || data.nutrition_plan || null;
      const t = p?.targets || data.targets || {};
      setDraft((prev) => ({
        ...prev,
        calories_target: t.calories_target ?? "",
        protein_g: t.protein_g ?? "",
        carbs_g: t.carbs_g ?? "",
        fats_g: t.fats_g ?? "",
        fiber_g: t.fiber_g ?? "",
        water_oz: t.water_oz ?? "",
      }));
      if (p) setPlan(p);
      showToast?.("✨ AI targets generated. Review and hit Save.", "success");
    } catch (e) {
      showToast?.("AI suggestions failed: " + (e?.message || String(e)), "error");
    } finally {
      setAiBusy(false);
      setLoading(false);
    }
  };

  const cancelMealGeneration = async () => {
    if (!token) return;
    setCancelingMealGen(true);
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/nutrition/meal-plan/cancel`, {
        method: "POST",
        headers: authHeaders,
        cache: "no-store",
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setAiBusy(false);
      setBgStatus("cancelled");
      if (data?.meal_plan) {
        setMealPlan({
          ...data.meal_plan,
          meta: {
            ...(data.meal_plan.meta || {}),
            bg_status: data?.meal_plan?.meta?.bg_status || "cancelled",
          },
        });
        setBgStatus(data?.meal_plan?.meta?.bg_status || "cancelled");
      }
      await loadExistingMealPlan();
      await loadExistingGroceryList();
      showToast?.(data?.cancelled ? "🛑 Meal generation cancelled." : "Generation stopped.", "success");
    } catch (e) {
      showToast?.("Cancel failed: " + (e?.message || String(e)), "error");
    } finally {
      setLoading(false);
      setAiBusy(false);
      setCancelingMealGen(false);
    }
  };

  const generateMealPlan = async () => {
    setAiBusy(true);
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/nutrition/meal-plan/ai/generate`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          days: Math.max(1, Math.min(30, Number(mealDays) || 7)),
          generate_grocery: false,
          auto_regen: !!autoMealRegen,
          auto_grocery: !!autoGroceryAfterMeal,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMealPlan(data.meal_plan || null);
      setBgStatus(data?.meal_plan?.meta?.bg_status || null);

      if (data?.background?.already_running) {
        showToast?.("⏳ A meal plan is already generating in the background. Please wait for it to finish.", "info");
      } else if (data?.background?.started) {
        showToast?.("✅ Today is ready. Generating the remaining days in the background…", "success");
      } else {
        showToast?.("✅ Meal plan generated.", "success");
      }

      if (
        (data?.meal_plan?.meta?.bg_status || null) !== "running" &&
        autoMealRegen &&
        autoGroceryAfterMeal &&
        data?.meal_plan?.meal_plan_id
      ) {
        try {
          await fetch(
            `${BACKEND_URL}/api/nutrition/grocery-list/from-meal-plan/${encodeURIComponent(data.meal_plan.meal_plan_id)}`,
            { method: "POST", headers: authHeaders }
          );
        } catch {}
      }

      await loadExistingMealPlan();
      await loadExistingGroceryList();
    } catch (e) {
      showToast?.("Generate failed: " + (e?.message || String(e)), "error");
    } finally {
      setAiBusy(false);
      setLoading(false);
    }
    setBgStatus("completed");
  };

  const regenerateMealPlanViaModify = async () => {
    setAiBusy(true);
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/nutrition/ai/modify`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "regenerate_meal_plan",
          days: Math.max(1, Math.min(30, Number(mealDays) || 7)),
          note: mealChangeNote || "",
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const mp = data.meal_plan || data.mealPlan || null;
      const gl = data.grocery_list || data.groceryList || null;
      if (mp) {
        setMealPlan(mp);
        setBgStatus(mp?.meta?.bg_status || null);
      }
      if (gl) setGrocery(gl);
      await loadExistingMealPlan();
      await loadExistingGroceryList();
      showToast?.("✅ New meal plan generated.", "success");
    } catch (e) {
      showToast?.("Regenerate failed: " + (e?.message || String(e)), "error");
    } finally {
      setAiBusy(false);
      setLoading(false);
    }
    setBgStatus("completed");
  };

  const replaceFoodOrIngredient = async () => {
    setAiBusy(true);
    if (!token) return;
    const find = replaceFrom.trim();
    const rep = replaceTo.trim();
    if (!find || !rep) {
      showToast?.("Enter both: what to replace, and what to replace it with.", "error");
      setAiBusy(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/nutrition/ai/modify`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "replace_food",
          find,
          replace_with: rep,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const mp = data.meal_plan || data.mealPlan || null;
      const gl = data.grocery_list || data.groceryList || null;
      if (mp) {
        setMealPlan(mp);
        setBgStatus(mp?.meta?.bg_status || null);
      }
      if (gl) setGrocery(gl);
      await loadExistingMealPlan();
      await loadExistingGroceryList();
      showToast?.("✅ Updated meal plan.", "success");
    } catch (e) {
      showToast?.("Replace failed: " + (e?.message || String(e)), "error");
    } finally {
      setAiBusy(false);
      setLoading(false);
    }
    setBgStatus("completed");
  };

  const openReplaceMealModal = (target) => {
    setMealActionTarget(target);
    setMealReplaceSearch("");
    setMealReplaceRecipeId("");
    setMealReplaceLibrary([]);
    setMealActionModal(true);
  };

  const closeMealActionModal = () => {
    setMealActionModal(false);
    setMealActionTarget(null);
    setMealReplaceSearch("");
    setMealReplaceLibrary([]);
    setMealReplaceRecipeId("");
  };

  const searchMealReplacementRecipes = async () => {
    if (!token || !mealActionTarget?.meal_type) return;
    try {
      const qs = new URLSearchParams();
      qs.set("meal_type", mealActionTarget.meal_type);
      if (mealReplaceSearch.trim()) qs.set("q", mealReplaceSearch.trim());
      const res = await fetch(`${BACKEND_URL}/api/nutrition/library/search?${qs.toString()}`, {
        headers: authHeaders,
        cache: "no-store",
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMealReplaceLibrary(Array.isArray(data?.recipes) ? data.recipes : []);
    } catch (e) {
      showToast?.("Recipe search failed: " + (e?.message || String(e)), "error");
    }
  };

  const deleteMealItem = async (target) => {
    if (!token || !mealPlan?.meal_plan_id || !target) return;
    if (!window.confirm("Delete this meal from the meal plan?")) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/nutrition/meal-plan/item/delete`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          meal_plan_id: mealPlan.meal_plan_id,
          day: target.day,
          meal_index: target.mealIndex,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      if (data?.meal_plan) {
        setMealPlan(data.meal_plan);
        setBgStatus(data?.meal_plan?.meta?.bg_status || null);
      } else {
        await loadExistingMealPlan();
      }
      await loadExistingGroceryList();
      showToast?.("✅ Meal deleted.", "success");
    } catch (e) {
      showToast?.("Delete meal failed: " + (e?.message || String(e)), "error");
    } finally {
      setLoading(false);
    }
  };

  const replaceMealItem = async () => {
    if (!token || !mealPlan?.meal_plan_id || !mealActionTarget) return;
    if (!mealReplaceRecipeId) {
      showToast?.("Choose a replacement recipe.", "error");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/nutrition/meal-plan/item/replace`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          meal_plan_id: mealPlan.meal_plan_id,
          day: mealActionTarget.day,
          meal_index: mealActionTarget.mealIndex,
          recipe_id: mealReplaceRecipeId,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      if (data?.meal_plan) {
        setMealPlan(data.meal_plan);
        setBgStatus(data?.meal_plan?.meta?.bg_status || null);
      } else {
        await loadExistingMealPlan();
      }
      await loadExistingGroceryList();
      closeMealActionModal();
      showToast?.("✅ Meal replaced.", "success");
    } catch (e) {
      showToast?.("Replace meal failed: " + (e?.message || String(e)), "error");
    } finally {
      setLoading(false);
    }
  };

  const openGroceryDetailModal = (item, category, idx) => {
    const itemId = item.item_id || item.id || `${category}-${item.name || "item"}-${idx}`;
    const draftItem = groceryDraft[itemId] || normalizeGroceryItem(item, itemId);
    setGroceryDetailModal({
      itemId,
      groceryListId: grocery?.grocery_list_id,
      item: draftItem,
      originalCategory: category,
    });
  };

  const closeGroceryDetailModal = () => {
    setGroceryDetailModal(null);
  };

  const saveGroceryDetail = async () => {
    if (!groceryDetailModal?.groceryListId || !groceryDetailModal?.itemId) return;
    await patchGroceryItem(groceryDetailModal.groceryListId, groceryDetailModal.itemId, {
      name: groceryDetailModal.item.name,
      quantity: groceryDetailModal.item.quantity,
      unit: groceryDetailModal.item.unit,
      category: groceryDetailModal.item.category,
      checked: groceryDetailModal.item.checked,
    });
    setGroceryDetailModal(null);
  };

  const renderRecipe = (r, idx) => {
    if (!r) return null;
    const ingredients = Array.isArray(r.ingredients) ? r.ingredients : [];
    return (
      <div key={idx} className="card" style={{ padding: 14, marginTop: 10, background: "var(--bg-card)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontWeight: 800 }}>{r.name || "Recipe"}</div>
          <div className="body-small" style={{ color: "var(--text-muted)" }}>
            {r.servings ? `${r.servings} servings` : ""}
          </div>
        </div>
        <div className="body-small" style={{ marginTop: 6, color: "var(--text-secondary)" }}>
          {r.calories != null ? `Calories: ${r.calories}` : ""}
          {r.protein_g != null ? ` • P: ${r.protein_g}g` : ""}
          {r.carbs_g != null ? ` • C: ${r.carbs_g}g` : ""}
          {r.fats_g != null ? ` • F: ${r.fats_g}g` : ""}
        </div>
        {ingredients.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Ingredients</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {ingredients.map((ing, i) => (
                <li key={i} className="body-small" style={{ color: "var(--text-secondary)", marginBottom: 4 }}>
                  {ing.quantity != null ? `${ing.quantity} ` : ""}
                  {ing.unit ? `${ing.unit} ` : ""}
                  {ing.name || ""}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const groceryItemsFlat = useMemo(() => {
    if (!grocery) return [];
    if (Array.isArray(grocery.items)) {
      return grocery.items.map((it, idx) =>
        normalizeGroceryItem(it, it?.item_id || it?.id || `${(it?.category || "Other")}-${(it?.name || "item")}-${idx}`)
      );
    }
    if (Array.isArray(grocery.groups)) {
      const out = [];
      for (const g of grocery.groups) {
        for (const it of g.items || []) {
          const merged = { ...it, category: it.category || g.category || "Other" };
          out.push(
            normalizeGroceryItem(
              merged,
              merged?.item_id || merged?.id || `${(merged?.category || "Other")}-${(merged?.name || "item")}-${out.length}`
            )
          );
        }
      }
      return out;
    }
    return [];
  }, [grocery]);

  const groceryGroups = useMemo(() => {
    if (!grocery) return [];
    if (Array.isArray(grocery.groups)) return grocery.groups;
    const byCat = {};
    for (const it of groceryItemsFlat) {
      const cat = (it.category || "Other").toString();
      byCat[cat] = byCat[cat] || [];
      byCat[cat].push(it);
    }
    return Object.keys(byCat).sort((a, b) => a.localeCompare(b)).map((cat) => ({
      category: cat,
      items: byCat[cat].slice().sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
    }));
  }, [grocery, groceryItemsFlat]);

  const mealDaysList = Array.isArray(mealPlan?.meals) ? mealPlan.meals : [];
  const activeDay = mealDaysList[selectedDayIndex] || null;
  const prevDay = () => setSelectedDayIndex((prev) => Math.max(0, prev - 1));
  const nextDay = () => setSelectedDayIndex((prev) => Math.min(mealDaysList.length - 1, prev + 1));

  const renderMealCard = (meal, dayNum, idx, keyPrefix = "meal") => (
    <div key={`${keyPrefix}-${idx}`} className="nh-meal-card">
      <div className="nh-meal-card__head">
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="nh-meal-title-row">
            <div style={{ fontWeight: 800, overflowWrap: "anywhere" }}>{meal.title || meal.meal_type}</div>
            <div className="nh-meal-icon-actions">
              <button
                type="button"
                className="nh-icon-btn"
                aria-label="Replace meal"
                title="Replace meal"
                onClick={() =>
                  openReplaceMealModal({
                    day: dayNum,
                    mealIndex: idx,
                    meal_type: meal.meal_type,
                    title: meal.title,
                  })
                }
                disabled={bgStatus === "running"}
              >
                🔧
              </button>
              <button
                type="button"
                className="nh-icon-btn nh-icon-btn-danger"
                aria-label="Delete meal"
                title="Delete meal"
                onClick={() =>
                  deleteMealItem({
                    day: dayNum,
                    mealIndex: idx,
                    meal_type: meal.meal_type,
                    title: meal.title,
                  })
                }
                disabled={bgStatus === "running"}
              >
                🗑
              </button>
            </div>
          </div>
          <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 4 }}>
            {(meal.meal_type || "meal").toString().toUpperCase()}
          </div>
        </div>
      </div>

      {Array.isArray(meal.recipes) && meal.recipes.length ? (
        <>
          <div className="body-small" style={{ color: "var(--text-secondary)", marginTop: 6, overflowWrap: "anywhere" }}>
            {meal.recipes.map((r) => r.name || "Recipe").join(", ")}
          </div>
          <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
            {meal.recipes.map((r, ridx) => renderRecipe(r, `${dayNum}-${meal.meal_type}-${ridx}`))}
          </div>
        </>
      ) : (
        <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 6 }}>
          Recipes will appear below once generated.
        </div>
      )}
    </div>
  );

  return (
    <div className="container nutrition-hub" style={{ maxWidth: 1100 }}>
      <SpinnerKeyframes />

      <div className="nh-header">
        <div>
          <div className="heading-2" style={{ margin: 0 }}>Nutrition</div>
          <div className="body-small" style={{ color: "var(--text-muted)" }}>
            Targets, meal plans, grocery lists, and AI modifications in one place.
          </div>
        </div>

        <div className="nh-inline-actions">
          <button
            className="btn-secondary"
            type="button"
            onClick={async () => {
              await Promise.all([loadPlan(), loadExistingMealPlan(), loadExistingGroceryList(), loadNutritionSettings()]);
            }}
            disabled={loading || busy}
          >
            Refresh
          </button>
          <button
            className="btn-primary"
            type="button"
            onClick={openOrCreateGroceryList}
            disabled={loading || busy || (!grocery && !mealPlan?.meal_plan_id)}
          >
            {grocery ? "Open Grocery List" : "Create Grocery List"}
          </button>
        </div>
      </div>

      {loading && (
        <div className="card" style={{ padding: 14, marginTop: 14 }}>
          <div className="body-medium">Loading…</div>
        </div>
      )}

      <div className="nh-page-stack">
        <section id="nutrition-mealplan" className="card nh-section-card nh-bordered-section" style={{ padding: 16, marginTop: 14 }}>
          {showMealPlanGeneratingBanner && (
            <div className="nh-generating-banner">
              <div style={{ fontWeight: 800, display: "flex", gap: 10, alignItems: "center" }}>
                <InlineSpinner size={16} /> Generating additional days in the background…
              </div>
              <div className="body-small" style={{ color: "var(--text-secondary)", marginTop: 6 }}>
                Today is ready now. We will notify you when the full plan and grocery list finish.
              </div>
            </div>
          )}

          <div className="nh-section-head nh-section-line">
            <div className="nh-section-copy">
              <div className="heading-4" style={{ marginBottom: 0 }}>Meal Plan</div>
              <div className="body-small nh-mobile-hide-copy" style={{ color: "var(--text-muted)", marginTop: 6 }}>
                Generate a meal plan and manage meals. Grocery list opens in a popup.
              </div>
            </div>

            <div className="nh-inline-actions">
              {!mealPlan ? (
                <button type="button" className="btn-primary" onClick={generateMealPlan} disabled={loading || busy}>
                  {aiBusy ? (
                    <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                      <InlineSpinner size={16} /> Generate Meal Plan
                    </span>
                  ) : (
                    "Generate Meal Plan"
                  )}
                </button>
              ) : (
                <button type="button" className="btn-primary" onClick={regenerateMealPlanViaModify} disabled={loading || busy}>
                  {aiBusy ? (
                    <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                      <InlineSpinner size={16} /> Generate New Meal Plan
                    </span>
                  ) : (
                    "Generate New Meal Plan"
                  )}
                </button>
              )}

              {showMealPlanGeneratingBanner && (
                <button type="button" className="btn-secondary" onClick={cancelMealGeneration} disabled={loading}>
                  Cancel Generation
                </button>
              )}

              <button
                type="button"
                className="btn-secondary"
                onClick={async () => {
                  if (!window.confirm("Delete the active meal plan (and its derived grocery lists)?")) return;
                  try {
                    await fetch(`${BACKEND_URL}/api/nutrition/meal-plan/active`, { method: "DELETE", headers: authHeaders });
                    setMealPlan(null);
                    setBgStatus("completed");
                    setGrocery(null);
                    showToast?.("✅ Meal plan deleted.", "success");
                  } catch {
                    showToast?.("Delete failed", "error");
                  }
                }}
                disabled={loading}
              >
                Delete active meal plan
              </button>
            </div>
          </div>

          <div className="nh-three-col" style={{ marginTop: 14 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="body-small" style={{ color: "var(--text-secondary)" }}>Days</label>
              <input
                className="form-input"
                type="number"
                min="1"
                max="30"
                value={mealDays}
                onChange={(e) => setMealDays(Number(e.target.value))}
              />
            </div>

            <label className="body-small nh-toggle-row">
              <input
                type="checkbox"
                checked={autoMealRegen}
                onChange={async (e) => {
                  setAutoMealRegen(e.target.checked);
                  await saveNutritionSettings({ auto_regen: e.target.checked });
                }}
              />
              Auto regenerate meal plan
            </label>

            <label className="body-small nh-toggle-row" style={{ color: autoMealRegen ? "var(--text-secondary)" : "var(--text-muted)" }}>
              <input
                type="checkbox"
                checked={autoGroceryAfterMeal}
                onChange={async (e) => {
                  setAutoGroceryAfterMeal(e.target.checked);
                  await saveNutritionSettings({ auto_grocery: e.target.checked });
                }}
                disabled={busy || !autoMealRegen}
              />
              Auto grocery after meal plan
            </label>
          </div>

          {mealPlan ? (
            <>
              <div className="form-group" style={{ marginTop: 14, marginBottom: 0 }}>
                <label className="body-small" style={{ color: "var(--text-secondary)" }}>
                  What should change in the new plan?
                </label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={mealChangeNote}
                  onChange={(e) => setMealChangeNote(e.target.value)}
                  placeholder='Example: "More high-protein lunches, less dairy, and quicker dinners."'
                />
              </div>

              <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 12 }}>
                Auto regenerate: {autoMealRegen ? "On" : "Off"} • Auto grocery after meal plan: {autoGroceryAfterMeal ? "On" : "Off"}
              </div>
            </>
          ) : null}

          {!mealPlan ? (
            <div className="body-medium" style={{ color: "var(--text-secondary)", marginTop: 12 }}>
              No meal plan yet. Click “Generate Meal Plan”.
            </div>
          ) : (
            <div style={{ marginTop: 14, display: "grid", gap: 12 }}>
              {isMobile ? (
                <div className="nh-day-card">
                  <div className="nh-day-selector">
                    <button type="button" className="nh-day-arrow" onClick={prevDay} disabled={selectedDayIndex === 0}>‹</button>
                    <div className="nh-day-selector-center">
                      <div className="body-small" style={{ color: "var(--text-muted)" }}>Selected day</div>
                      <div className="heading-4" style={{ margin: 0 }}>Day {activeDay?.day ?? 1}</div>
                    </div>
                    <button type="button" className="nh-day-arrow" onClick={nextDay} disabled={selectedDayIndex >= mealDaysList.length - 1}>›</button>
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <MealCardCarousel
                      items={activeDay?.items || []}
                      renderItem={(meal, idx) => renderMealCard(meal, activeDay?.day, idx, `mobile-${activeDay?.day}`)}
                    />
                  </div>
                </div>
              ) : (
                mealDaysList.map((day) => (
                  <div key={day.day} className="nh-day-card">
                    <div style={{ fontWeight: 900, marginBottom: 8 }}>Day {day.day}</div>
                    <div style={{ display: "grid", gap: 10 }}>
                      {(day.items || []).map((meal, idx) => renderMealCard(meal, day.day, idx, `desktop-${day.day}`))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </section>

        

        <section id="nutrition-modify" className="card nh-section-card nh-bordered-section" style={{ padding: 16, marginTop: 14 }}>
          <div className="nh-section-head nh-section-line">
            <div className="nh-section-copy">
              <div className="heading-4" style={{ marginBottom: 0 }}>Replace Food / Ingredient</div>
              <div className="body-small nh-mobile-hide-copy" style={{ color: "var(--text-muted)", marginTop: 6 }}>
                Replace ingredients or foods in the current meal plan.
              </div>
            </div>
          </div>

          <div className="nh-inner-divider" style={{ marginTop: 0, paddingTop: 0, borderTop: "none" }}>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Replace a food / ingredient</div>
            <div className="nh-two-col">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="body-small" style={{ color: "var(--text-secondary)" }}>Replace this</label>
                <input
                  className="form-input"
                  value={replaceFrom}
                  onChange={(e) => setReplaceFrom(e.target.value)}
                  placeholder="e.g. chicken, milk, broccoli"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="body-small" style={{ color: "var(--text-secondary)" }}>With this</label>
                <input
                  className="form-input"
                  value={replaceTo}
                  onChange={(e) => setReplaceTo(e.target.value)}
                  placeholder="e.g. turkey, oat milk, asparagus"
                />
              </div>
            </div>

            <div className="nh-inline-actions" style={{ justifyContent: "flex-end", marginTop: 12 }}>
              <button type="button" className="btn-primary" onClick={replaceFoodOrIngredient} disabled={loading || busy}>
                {aiBusy ? (
                  <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                    <InlineSpinner size={16} /> Replace in current meal plan
                  </span>
                ) : (
                  "Replace in current meal plan"
                )}
              </button>
            </div>
          </div>
        </section>
        <section id="nutrition-targets" className="card nh-section-card nh-bordered-section" style={{ padding: 16, marginTop: 14 }}>
          <div className="nh-section-head nh-section-line">
            <div className="nh-section-copy">
              <div className="heading-4" style={{ marginBottom: 0 }}>Nutrition Targets</div>
              <div className="body-small nh-mobile-hide-copy" style={{ color: "var(--text-muted)", marginTop: 6 }}>
                Update calories, macros, fiber, and water targets.
              </div>
            </div>
          </div>

          <div className="nh-two-col" style={{ marginTop: 12 }}>
            {[
              ["calories_target", "Calories"],
              ["protein_g", "Protein (g)"],
              ["carbs_g", "Carbs (g)"],
              ["fats_g", "Fats (g)"],
              ["fiber_g", "Fiber (g)"],
              ["water_oz", "Water (oz)"],
            ].map(([k, label]) => (
              <div key={k} className="form-group" style={{ marginBottom: 0 }}>
                <label className="body-small" style={{ color: "var(--text-secondary)" }}>{label}</label>
                <input
                  className="form-input"
                  type="number"
                  value={draft[k] === "" ? "" : Number(draft[k])}
                  onChange={(e) => setDraft((p) => ({ ...p, [k]: e.target.value }))}
                />
              </div>
            ))}
          </div>

          <div className="nh-inline-actions" style={{ justifyContent: "flex-end", marginTop: 14 }}>
            <button type="button" className="btn-secondary" onClick={() => saveTargets(false)} disabled={loading || aiBusy || bgStatus === "running"}>
              Save
            </button>
            <button type="button" className="btn-primary" onClick={aiSuggestTargets} disabled={loading || busy}>
              AI suggestions
            </button>
          </div>
        </section>
      </div>

      {showGroceryModal && (
        <div className="modal-overlay" onMouseDown={() => setShowGroceryModal(false)}>
          <div className="modal-content ct-form-modal nh-modal-content" onMouseDown={(e) => e.stopPropagation()}>
            <div className="nh-modal-header">
              <div>
                <h3 className="heading-3" style={{ margin: 0 }}>Grocery List</h3>
                <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 6 }}>
                  Tap an item to view and edit details.
                </div>
              </div>
              <div className="nh-inline-actions nh-modal-actions-top">
                <button type="button" className="nh-plus-btn" aria-label="Add grocery item" onClick={() => setShowAddGroceryModal(true)}>+</button>
                <button type="button" className="nh-modal-close" aria-label="Close" onClick={() => setShowGroceryModal(false)}>✕</button>
              </div>
            </div>

            <div className="nh-modal-body">
              <div className="nh-inline-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={!grocery?.grocery_list_id || loading || busy}
                  onClick={async () => {
                    if (!grocery?.grocery_list_id) return;
                    if (!window.confirm("Delete the active grocery list?")) return;
                    try {
                      await fetch(`${BACKEND_URL}/api/nutrition/grocery-list/${encodeURIComponent(grocery.grocery_list_id)}`, {
                        method: "DELETE",
                        headers: authHeaders,
                      });
                      setGrocery(null);
                      setShowGroceryModal(false);
                      showToast?.("✅ Grocery list deleted.", "success");
                    } catch {
                      showToast?.("Delete failed", "error");
                    }
                  }}
                >
                  Delete grocery list
                </button>
              </div>

              {!grocery ? (
                <div className="body-medium" style={{ color: "var(--text-secondary)", marginTop: 12 }}>
                  No grocery list yet. Generate a meal plan, then open the grocery list.
                </div>
              ) : groceryGroups.length === 0 ? (
                <div className="body-medium" style={{ color: "var(--text-muted)", marginTop: 12 }}>
                  Grocery list is empty. Tap the plus button to add an item.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
                  {groceryGroups.map((g) => (
                    <div key={g.category} className="nh-day-card">
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>{g.category}</div>
                      <div style={{ display: "grid", gap: 10 }}>
                        {(g.items || []).map((it, idx) => {
                          const itemId = it.item_id || it.id || `${g.category}-${(it.name || "item")}-${idx}`;
                          const itemDraft = groceryDraft[itemId] || it;
                          return (
                            <button
                              type="button"
                              key={`${g.category}-${idx}-${it.name}-${itemId}`}
                              className="nh-grocery-tile"
                              onClick={() => openGroceryDetailModal(itemDraft, g.category, idx)}
                            >
                              <div className="nh-grocery-tile-main">
                                <input
                                  type="checkbox"
                                  checked={Boolean(itemDraft.checked)}
                                  onChange={async (e) => {
                                    e.stopPropagation();
                                    const checked = e.target.checked;
                                    setGroceryDraft((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || itemDraft), checked } }));
                                    await patchGroceryItem(grocery.grocery_list_id, itemId, { checked });
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div style={{ minWidth: 0, textAlign: "left" }}>
                                  <div style={{ fontWeight: 800, textDecoration: itemDraft.checked ? "line-through" : "none", overflowWrap: "anywhere" }}>
                                    {itemDraft.name}
                                  </div>
                                  <div className="body-small" style={{ color: "var(--text-muted)" }}>
                                    {itemDraft.quantity !== "" && itemDraft.quantity != null ? `${itemDraft.quantity} ` : ""}
                                    {itemDraft.unit || ""}
                                  </div>
                                </div>
                              </div>
                              <div className="body-small" style={{ color: "var(--text-muted)" }}>›</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showAddGroceryModal && (
        <div className="modal-overlay" onMouseDown={() => setShowAddGroceryModal(false)}>
          <div className="modal-content ct-form-modal nh-modal-content nh-replace-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="nh-modal-header">
              <div>
                <h3 className="heading-3" style={{ margin: 0 }}>Add Grocery Item</h3>
                <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 6 }}>
                  Add one item to the active grocery list.
                </div>
              </div>
              <button type="button" className="nh-modal-close" aria-label="Close" onClick={() => setShowAddGroceryModal(false)}>✕</button>
            </div>
            <div className="nh-modal-body">
              <div className="nh-modal-form-grid">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>Add item</label>
                  <input className="form-input" value={newGroceryItem.name} onChange={(e) => setNewGroceryItem((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. bananas" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>Qty</label>
                  <input className="form-input" value={newGroceryItem.quantity} onChange={(e) => setNewGroceryItem((p) => ({ ...p, quantity: e.target.value }))} placeholder="e.g. 2" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>Unit</label>
                  <input className="form-input" value={newGroceryItem.unit} onChange={(e) => setNewGroceryItem((p) => ({ ...p, unit: e.target.value }))} placeholder="e.g. lbs" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>Category</label>
                  <input className="form-input" value={newGroceryItem.category} onChange={(e) => setNewGroceryItem((p) => ({ ...p, category: e.target.value }))} placeholder="e.g. Produce" />
                </div>
              </div>
              <div className="nh-modal-footer">
                <button className="btn-secondary" type="button" onClick={() => setShowAddGroceryModal(false)}>Cancel</button>
                <button type="button" className="btn-primary" onClick={() => addGroceryItem(grocery?.grocery_list_id, newGroceryItem)} disabled={loading || busy || !newGroceryItem.name.trim()}>
                  Add Item
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {groceryDetailModal && (
        <div className="modal-overlay" onMouseDown={closeGroceryDetailModal}>
          <div className="modal-content ct-form-modal nh-modal-content nh-replace-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="nh-modal-header">
              <div>
                <h3 className="heading-3" style={{ margin: 0 }}>Grocery Item Details</h3>
                <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 6 }}>
                  Edit and save this grocery item.
                </div>
              </div>
              <button type="button" className="nh-modal-close" aria-label="Close" onClick={closeGroceryDetailModal}>✕</button>
            </div>
            <div className="nh-modal-body">
              <label className="nh-check-row">
                <input
                  type="checkbox"
                  checked={Boolean(groceryDetailModal.item.checked)}
                  onChange={(e) =>
                    setGroceryDetailModal((prev) => ({
                      ...prev,
                      item: { ...prev.item, checked: e.target.checked },
                    }))
                  }
                />
                Mark as completed
              </label>

              <div className="nh-modal-form-grid">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>Item</label>
                  <input
                    className="form-input"
                    value={groceryDetailModal.item.name || ""}
                    onChange={(e) =>
                      setGroceryDetailModal((prev) => ({
                        ...prev,
                        item: { ...prev.item, name: e.target.value },
                      }))
                    }
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>Qty</label>
                  <input
                    className="form-input"
                    value={groceryDetailModal.item.quantity ?? ""}
                    onChange={(e) =>
                      setGroceryDetailModal((prev) => ({
                        ...prev,
                        item: { ...prev.item, quantity: e.target.value === "" ? "" : Number(e.target.value) || e.target.value },
                      }))
                    }
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>Unit</label>
                  <input
                    className="form-input"
                    value={groceryDetailModal.item.unit || ""}
                    onChange={(e) =>
                      setGroceryDetailModal((prev) => ({
                        ...prev,
                        item: { ...prev.item, unit: e.target.value },
                      }))
                    }
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>Category</label>
                  <input
                    className="form-input"
                    value={groceryDetailModal.item.category || ""}
                    onChange={(e) =>
                      setGroceryDetailModal((prev) => ({
                        ...prev,
                        item: { ...prev.item, category: e.target.value },
                      }))
                    }
                  />
                </div>
              </div>

              <div className="nh-modal-footer">
                <button className="btn-secondary" type="button" onClick={closeGroceryDetailModal}>Cancel</button>
                <button className="btn-primary" type="button" onClick={saveGroceryDetail} disabled={loading || busy}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {mealActionModal && (
        <div className="modal-overlay" onMouseDown={closeMealActionModal}>
          <div className="modal-content ct-form-modal nh-modal-content nh-replace-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="nh-modal-header">
              <div>
                <h3 className="heading-3" style={{ margin: 0 }}>Replace Meal</h3>
                <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 6 }}>
                  Pick a replacement recipe for this meal.
                </div>
              </div>
              <button type="button" className="nh-modal-close" aria-label="Close" onClick={closeMealActionModal}>✕</button>
            </div>
            <div className="nh-modal-body">
              <div className="nh-modal-box">
                <div style={{ fontWeight: 800, overflowWrap: "anywhere" }}>{mealActionTarget?.title || "Meal"}</div>
                <div className="body-small" style={{ color: "var(--text-secondary)", marginTop: 4 }}>
                  {mealActionTarget?.meal_type || "meal"}
                </div>
              </div>

              <div className="nh-search-row" style={{ marginTop: 12 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>Search replacement recipes</label>
                  <input className="form-input" value={mealReplaceSearch} onChange={(e) => setMealReplaceSearch(e.target.value)} placeholder="chicken bowl, oatmeal, salad…" />
                </div>
                <button className="btn-secondary" type="button" onClick={searchMealReplacementRecipes} style={{ height: 48 }} disabled={loading}>
                  Search
                </button>
              </div>

              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="body-small" style={{ color: "var(--text-secondary)" }}>Choose a replacement</label>
                <select className="form-input" value={mealReplaceRecipeId} onChange={(e) => setMealReplaceRecipeId(e.target.value)}>
                  <option value="">Select a recipe…</option>
                  {mealReplaceLibrary.map((r) => (
                    <option key={r.recipe_id} value={r.recipe_id}>
                      {r.name}{r.calories != null ? ` — ${r.calories} cal` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="nh-modal-footer">
                <button className="btn-secondary" type="button" onClick={() => { deleteMealItem(mealActionTarget); closeMealActionModal(); }} disabled={loading}>
                  Delete Instead
                </button>
                <div className="nh-inline-actions">
                  <button className="btn-secondary" type="button" onClick={closeMealActionModal} disabled={loading}>Cancel</button>
                  <button className="btn-primary" type="button" onClick={replaceMealItem} disabled={loading || !mealReplaceRecipeId}>Replace Meal</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
